import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAppSelector } from 'src/services/redux/app/store';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

export const useDashboardList = (params: any, onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id, ...params, user_id: user?.id, limitHomeworkPast: 5 };
  const requestUrl = REACT_APP_API_URL + '/dashboard/list';

  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { 
      params: requestParams,
      headers: { Authorization: `Bearer ${tokens?.access?.token}` }
    });
    return res?.data || {};
  };
  return useQuery(['dashboard-list'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
  });
};

// Bugünkü doluluk oranı
export const useOccupancyToday = (onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id };
  const requestUrl = REACT_APP_API_URL + '/dashboard/occupancyToday';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { 
      params: requestParams,
      headers: { Authorization: `Bearer ${tokens?.access?.token}` }
    });
    
    return res?.data || {};
  };
  return useQuery(['dashboard-occupancy-today'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 60000, // 1 dakika
    retry: 1,
  });
};

// Bugün check-in yapacak misafir sayısı
export const useCheckinsToday = (onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id };
  const requestUrl = REACT_APP_API_URL + '/dashboard/checkinsToday';

  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { 
      params: requestParams,
      headers: { Authorization: `Bearer ${tokens?.access?.token}` }
    });
    return res?.data || {};
  };
  return useQuery(['dashboard-checkins-today'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 60000,
    retry: 1,
  });
};

// Bugün check-out yapacak misafir sayısı
export const useCheckoutsToday = (onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id };
  const requestUrl = REACT_APP_API_URL + '/dashboard/checkoutsToday';

  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { 
      params: requestParams,
      headers: { Authorization: `Bearer ${tokens?.access?.token}` }
    });
    return res?.data || {};
  };
  return useQuery(['dashboard-checkouts-today'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 60000,
    retry: 1,
  });
};

// Önümüzdeki 7 günün rezervasyon grafiği
export const useUpcomingReservations = (onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id };
  const requestUrl = REACT_APP_API_URL + '/dashboard/upcomingReservations';

  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { 
      params: requestParams,
      headers: { Authorization: `Bearer ${tokens?.access?.token}` }
    });
    return res?.data || {};
  };
  return useQuery(['dashboard-upcoming-reservations'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 300000, // 5 dakika
    retry: 1,
  });
};

// Bugünkü toplam gelir
export const useRevenueToday = (onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id };
  const requestUrl = REACT_APP_API_URL + '/dashboard/revenueToday';

  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { 
      params: requestParams,
      headers: { Authorization: `Bearer ${tokens?.access?.token}` }
    });
    return res?.data || {};
  };
  return useQuery(['dashboard-revenue-today'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 60000,
    retry: 1,
  });
};

// Bekleyen hizmet talepleri
export const useServiceRequests = (onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id };
  const requestUrl = REACT_APP_API_URL + '/dashboard/serviceRequests';

  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { 
      params: requestParams,
      headers: { Authorization: `Bearer ${tokens?.access?.token}` }
    });
    return res?.data || {};
  };
  return useQuery(['dashboard-service-requests'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 30000, // 30 saniye
    retry: 1,
  });
};

// Şu anda otelde kalan misafir sayısı
export const useInHouseGuests = (onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id };
  const requestUrl = REACT_APP_API_URL + '/dashboard/inHouseGuests';

  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { 
      params: requestParams,
      headers: { Authorization: `Bearer ${tokens?.access?.token}` }
    });
    return res?.data || {};
  };
  return useQuery(['dashboard-in-house-guests'], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: 60000,
    retry: 1,
  });
};

export const useTodosUpdate = (onSuccess?: any, onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/todos/update';
  return useMutation(
    (formPayload: any) => {
      const requestParams = { company_id: user?.company_id, ...formPayload, user_id: user?.id };
      return axios.post<any>(`${requestUrl}`, requestParams, {
        headers: { Authorization: `Bearer ${tokens?.access?.token}` }
      });
    },
    { onError, onSuccess },
  );
};

export const useInviteCreate = (onSuccess?: any, onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/company/invite/create';
  return useMutation(
    (formPayload: any) => {
      const requestParams = { ...formPayload, user_id: user?.id, company_id: user?.company_id };
      return axios.post<any>(`${requestUrl}`, requestParams, {
        headers: { Authorization: `Bearer ${tokens?.access?.token}` }
      });
    },
    { onError, onSuccess },
  );
};

// export const useCampaignLeadList = (onError?: any) => {
//   const { user } = useAppSelector((state) => state.auth);
//   const requestUrl = REACT_APP_API_URL + '/registration/campaign/list';
//   return useMutation(
//     (formPayload: any) => {
//       const requestParams = { ...formPayload, user_id: user?.id, company_id: user?.company_id };
//       return axios.get<any>(`${requestUrl}`, requestParams);
//     },
//     { onError },
//   );
// };

export const useCampaignLeadList = (onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id, user_id: user?.id };
  const requestUrl = REACT_APP_API_URL + '/registration/campaign/list';

  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { 
      params: requestParams,
      headers: { Authorization: `Bearer ${tokens?.access?.token}` }
    });
    return res?.data || {};
  };
  return useQuery(['campaign-list'], fetchData, {
    onError,
    refetchOnWindowFocus: false,
  });
};

export const useCampaignAddNote = (onSuccess?: any, onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/registration/campaign/addNote';
  return useMutation(
    (formPayload: any) => {
      const requestParams = { ...formPayload, user_name: user?.name, company_id: user?.company_id, user_id: user?.id };
      return axios.post<any>(`${requestUrl}`, requestParams, {
        headers: { Authorization: `Bearer ${tokens?.access?.token}` }
      });
    },
    { onError, onSuccess },
  );
};

export const useCampaignAddStatus = (onSuccess?: any, onError?: any) => {
  const { user, tokens } = useAppSelector((state) => state.auth);
  const requestUrl = REACT_APP_API_URL + '/registration/campaign/addStatus';
  return useMutation(
    (formPayload: any) => {
      const requestParams = { ...formPayload, user_name: user?.name, company_id: user?.company_id, user_id: user?.id };
      return axios.post<any>(`${requestUrl}`, requestParams, {
        headers: { Authorization: `Bearer ${tokens?.access?.token}` }
      });
    },
    { onError, onSuccess },
  );
};
